import { FC, useContext, useState } from "react";
import { RouteContext } from "./route-context";
import { Text } from "../../../components/text/text";
import { useTranslation } from "react-i18next";
import { Box, Button, Grid, makeStyles } from "@material-ui/core";
import { DriverLocationSelect } from "../../../components/selects/driver-location/driver-location-select";
import { PageHeading } from "../../../components/heading/page-heading";
import { Cancel, Check, Edit } from "@material-ui/icons";
import { EditRouteStopsContext } from "./edit-route-stops.context";
import classnames from "classnames";
import { ConfirmDialog } from "../../../components/dialogs/confirm-dialog";

interface RouteDetailsHeaderProps {}

const useStyles = makeStyles((theme) => ({
  fractionInfo: {
    color: theme.palette.primary.main,
  },
  button: {
    borderWidth: 0.5,
    borderRadius: 3,
  },
  primaryButton: {
    borderColor: theme.palette.primary.main,
  },
  cancelButton: {
    borderColor: theme.palette.error.main,
  },
}));

export const RouteDetailsHeader: FC<RouteDetailsHeaderProps> = (props) => {
  const { route, routeLoading, updateLocation } = useContext(RouteContext);
  const { isInEditMode, setIsInEditMode, saveChanges, cancelEditing } = useContext(EditRouteStopsContext);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();

  if (!route || routeLoading) {
    return null;
  }

  const onConfirmCancellation = () => {
    cancelEditing();
    setCancelDialogOpen(false);
  };

  return (
    <>
      <ConfirmDialog
        open={cancelDialogOpen}
        heading={t("routes_management.update.unsaved_data_prompt.title")}
        text={t("routes_management.update.unsaved_data_prompt.content")}
        confirmText={t("yes")}
        negativeText={t("no")}
        onConfirm={onConfirmCancellation}
        onClose={() => setCancelDialogOpen(false)}
      />
      <Grid item container direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1}>
        <Grid item>
          <PageHeading>{t("routes_management.title")}</PageHeading>
          <Text className={classes.fractionInfo} variant={"h6"}>
            {`${t("route_details.tour_number")}: ${route.tour_number} | ${t("route_details.material")}: ${
              route.material?.name || "-"
            }`}
          </Text>
          <Box mt={3}>
            <DriverLocationSelect driverLocationId={String(route.location_id)} onChange={updateLocation} />
          </Box>
        </Grid>
        <Grid item xs container direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
          {isInEditMode ? (
            <>
              <Grid item>
                <Button
                  type="button"
                  variant="outlined"
                  className={classnames(classes.button, classes.cancelButton)}
                  onClick={() => setCancelDialogOpen(true)}
                >
                  <Cancel color="error" />
                </Button>
              </Grid>
              <Grid item>
                <Button
                  type="button"
                  variant="outlined"
                  className={classnames(classes.button, classes.primaryButton)}
                  onClick={saveChanges}
                >
                  <Check color="primary" />
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item>
              <Button
                type="button"
                variant="outlined"
                className={classnames(classes.button, classes.primaryButton)}
                onClick={() => setIsInEditMode(true)}
              >
                <Edit color="primary" />
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};
